import axios from 'axios';

import { EDX_API_ENDPOINTS, CREDENTIALS_API_ENDPOINTS } from 'config';
import { getPanelLmsBaseUrl, getCredentialsBaseUrl } from 'helpers';
import { userSettingsService } from 'services/settings';

const fetchBadgesData = () => {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.BADGE_API}`;
  const requestOptions = {
    crossDomain: true,
    withCredentials: true,
    method: 'GET',
    url
  };
  return axios(requestOptions);
};

export const badgrService = {
  fetchBadgesData
};

const fetchLtiConfigurations = () => {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LTI_CONFIGURATION}`;
  const requestOptions = {
    crossDomain: true,
    withCredentials: true,
    method: 'GET',
    url
  };
  return axios(requestOptions);
};

export function createLtiConfiguration(data) {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LTI_CONFIGURATION}`;
  const requestOptions = {
    method: 'POST',
    withCredentials: true,
    headers: {
      'X-CSRFToken': localStorage.getItem('lms_csrf_token')
    },
    url,
    data: data
  };
  return axios(requestOptions);
}

export function patchLtiConfiguration(id, data) {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LTI_CONFIGURATION}/?id=${id}`;
  const requestOptions = {
    method: 'PATCH',
    withCredentials: true,
    headers: {
      'X-CSRFToken': localStorage.getItem('lms_csrf_token')
    },
    url,
    data: data
  };
  return axios(requestOptions);
}

export function deleteLtiConfiguration(params) {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LTI_CONFIGURATION}`;
  const requestOptions = {
    method: 'DELETE',
    withCredentials: true,
    headers: {
      'X-CSRFToken': localStorage.getItem('lms_csrf_token')
    },
    params: params,
    url
  };
  return axios(requestOptions);
}
export const ltiConfigurationService = {
  fetchLtiConfigurations,
  createLtiConfiguration,
  patchLtiConfiguration,
  deleteLtiConfiguration
};

const fetchPiiSharingConfigs = () => {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LTI_PII_SHARING}`;
  const requestOptions = {
    crossDomain: true,
    withCredentials: true,
    method: 'GET',
    url
  };
  return axios(requestOptions);
};

const updatePiiSharingConfig = (courseId, enabled) => {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LTI_PII_SHARING}`;
  const requestOptions = {
    method: 'POST',
    withCredentials: true,
    headers: {
      'X-CSRFToken': localStorage.getItem('lms_csrf_token')
    },
    url,
    data: { course_id: courseId, enabled }
  };
  return axios(requestOptions);
};

export const ltiPiiSharingService = {
  fetchPiiSharingConfigs,
  updatePiiSharingConfig
};

const refreshSession = () => {
  const url = `${getPanelLmsBaseUrl()}${EDX_API_ENDPOINTS.LOGIN_REFRESH}`;
  return axios.post(url, {}, { withCredentials: true });
};

const refreshCredentialsCsrfToken = async () => {
  await refreshSession();
  const csrfRes = await userSettingsService.getCredentialsCsrfToken();

  const newToken = csrfRes?.data?.csrfToken;
  if (newToken) {
    localStorage.setItem('credentials_csrf_token', newToken);
  }
};

const credentialsAxios = axios.create();

credentialsAxios.interceptors.request.use((config) => {
  config.withCredentials = true;
  config.headers['X-CSRFToken'] = localStorage.getItem('credentials_csrf_token');
  return config;
});

// we need to refresh the session and CSRF token if the response is 401/403 and the request has not been retried
credentialsAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    if ((response?.status === 401 || response?.status === 403) && !config?._retry) {
      config._retry = true;
      await refreshCredentialsCsrfToken();
      return credentialsAxios(config);
    }

    return Promise.reject(error);
  }
);

const refreshStudioCsrfToken = async () => {
  await refreshSession();
  const csrfRes = await userSettingsService.getStudioCsrfToken();

  const newToken = csrfRes?.data?.csrf_token;
  if (newToken) {
    localStorage.setItem('studio_csrf_token', newToken);
  }
};

export const studioAxios = axios.create();

studioAxios.interceptors.request.use((config) => {
  config.withCredentials = true;
  config.headers['X-CSRFToken'] = localStorage.getItem('studio_csrf_token');
  return config;
});

// we need to refresh the session and CSRF token if the response is 401/403 and the request has not been retried
studioAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    if ((response?.status === 401 || response?.status === 403) && !config?._retry) {
      config._retry = true;
      await refreshStudioCsrfToken();
      return studioAxios(config);
    }

    return Promise.reject(error);
  }
);

const fetchBadgeConfigs = () =>
  credentialsAxios.get(`${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_CONFIGS}`);

const createBadgeConfig = (data, provider = 'accredible') =>
  credentialsAxios.post(
    `${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_CONFIGS}?provider=${provider}`,
    data
  );

const updateBadgeConfig = (id, data, provider = 'accredible') =>
  credentialsAxios.patch(
    `${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_CONFIGS}${id}/?provider=${provider}`,
    data
  );

const deleteBadgeConfig = (id, provider = 'accredible') =>
  credentialsAxios.delete(
    `${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_CONFIGS}${id}/?provider=${provider}`
  );

const fetchBadgeGroups = (provider) =>
  credentialsAxios.get(`${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_GROUPS}?provider=${provider}`);

const syncBadgeGroups = (configId, provider = 'accredible') =>
  credentialsAxios.post(
    `${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_CONFIGS}${configId}/sync_groups/?provider=${provider}`
  );

const updateBadgeGroup = (groupId, provider, data) =>
  credentialsAxios.patch(
    `${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_GROUPS}${groupId}/?provider=${provider}`,
    data
  );

const createBadgeRequirement = (data) =>
  credentialsAxios.post(`${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_REQUIREMENTS}`, data);

const deleteBadgeRequirement = (id) =>
  credentialsAxios.delete(`${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_REQUIREMENTS}${id}/`);

const updateBadgeRequirement = (id, data) =>
  credentialsAxios.patch(`${getCredentialsBaseUrl()}${CREDENTIALS_API_ENDPOINTS.BADGE_REQUIREMENTS}${id}/`, data);

// Validate course exists in LMS - returns 200 if exists, 404 if not
const validateCourseExists = (courseKey) => {
  const url = `${getPanelLmsBaseUrl()}api/courses/v1/courses/${encodeURIComponent(courseKey)}/`;
  return axios.get(url, { withCredentials: true });
};

export const badgesService = {
  fetchBadgeConfigs,
  createBadgeConfig,
  updateBadgeConfig,
  deleteBadgeConfig,
  fetchBadgeGroups,
  syncBadgeGroups,
  updateBadgeGroup,
  createBadgeRequirement,
  updateBadgeRequirement,
  deleteBadgeRequirement,
  validateCourseExists
};
